import { z } from 'zod';

/**
 * Worker public-API contracts. Endpoint paths come from spec §13.3.
 * Schemas Zod-validated at the Worker boundary; the same schemas type the
 * web UI's fetch wrappers in `apps/manager-web/src/lib/api.ts`.
 *
 * Phase 1 ships only the trigger shapes referenced by the harness. Phases
 * 3–6 grow this file.
 */

export const repoIdParam = z.string().uuid();

export const triggerFeatureCreated = z.object({
  feature_id: z.string().uuid(),
  repo_id: repoIdParam,
});
export type TriggerFeatureCreated = z.infer<typeof triggerFeatureCreated>;

export const triggerApproveEpic = z.object({
  epic_id: z.string().uuid(),
  repo_id: repoIdParam,
});
export type TriggerApproveEpic = z.infer<typeof triggerApproveEpic>;

export const triggerConfirmSprint = z.object({
  sprint_id: z.string().uuid(),
  repo_id: repoIdParam,
});
export type TriggerConfirmSprint = z.infer<typeof triggerConfirmSprint>;

export const healthResponse = z.object({
  status: z.literal('ok'),
  env: z.string(),
  ts: z.string(),
});
export type HealthResponse = z.infer<typeof healthResponse>;

/**
 * Worker response shape for all `/api/trigger/*` endpoints. The Worker writes
 * a `decisions` row to D1 and returns the row id so the caller can poll it.
 */
export const triggerEnqueuedResponse = z.object({
  queued: z.literal(true),
  id: z.string().uuid(),
});
export type TriggerEnqueuedResponse = z.infer<typeof triggerEnqueuedResponse>;

/** Worker response for `/api/kb/embed`. */
export const kbEmbedResponse = z.object({
  updated: z.number().int().nonnegative(),
  slugs: z.array(z.string()),
});
export type KbEmbedResponse = z.infer<typeof kbEmbedResponse>;

// ── Phase 5 — CLAUDE.md sync + drift ────────────────────────────────────────

/** Mode for the generator: `preview` returns files only; `github` opens a PR. */
export const kbSyncMode = z.enum(['preview', 'github']);
export type KbSyncMode = z.infer<typeof kbSyncMode>;

/**
 * `POST /api/kb/sync` request — sync a single repo. `repo_id` is required
 * to resolve `kb_applicability` overrides + the target install for the
 * GitHub-App write path. `preview` mode returns the generated content but
 * does not write anywhere.
 */
export const kbSyncRequest = z.object({
  repo_id: repoIdParam,
  mode: kbSyncMode.default('preview'),
});
export type KbSyncRequest = z.infer<typeof kbSyncRequest>;

export const kbGeneratedFile = z.object({
  path: z.string(),
  workspace: z.string(),
  content: z.string(),
  entries: z.array(
    z.object({
      slug: z.string(),
      severity: z.enum(['standard', 'recommended', 'optional']),
      effectiveSeverity: z.enum(['standard', 'recommended', 'optional']),
    }),
  ),
});
export type KbGeneratedFile = z.infer<typeof kbGeneratedFile>;

export const kbSyncResponse = z.object({
  repo_id: z.string().uuid(),
  mode: kbSyncMode,
  kbVersionHash: z.string(),
  generatedAt: z.string(),
  files: z.array(kbGeneratedFile),
  /** Set when `mode === 'github'` and a PR was opened. */
  pullRequestUrl: z.string().url().optional(),
  /** Set when the github path is wired but App credentials are unset. */
  warning: z.string().optional(),
});
export type KbSyncResponse = z.infer<typeof kbSyncResponse>;

/**
 * `POST /api/kb/sync-all` — iterate all rows in `repos`. Per-repo errors are
 * captured in `results[].error`; the endpoint still returns 200 (partial
 * success is the common case once multiple repos are managed).
 */
export const kbSyncAllRequest = z.object({
  mode: kbSyncMode.default('preview'),
});
export type KbSyncAllRequest = z.infer<typeof kbSyncAllRequest>;

export const kbSyncAllResponse = z.object({
  kbVersionHash: z.string(),
  generatedAt: z.string(),
  results: z.array(
    z.object({
      repo_id: z.string().uuid(),
      status: z.enum(['ok', 'error', 'skipped']),
      pullRequestUrl: z.string().url().optional(),
      error: z.string().optional(),
    }),
  ),
});
export type KbSyncAllResponse = z.infer<typeof kbSyncAllResponse>;

/**
 * `GET /api/kb/drift/:repo_id` — compares `repos.kb_version_hash` (the hash
 * stored on the last successful sync) against the live KB. `drift=true`
 * means the repo's on-disk CLAUDE.md set is stale; the next `kb:sync` will
 * produce the included `files`.
 */
export const kbDriftResponse = z.object({
  repo_id: z.string().uuid(),
  drift: z.boolean(),
  storedHash: z.string().nullable(),
  currentHash: z.string(),
  generatedAt: z.string(),
  files: z.array(kbGeneratedFile),
});
export type KbDriftResponse = z.infer<typeof kbDriftResponse>;
